import {useEffect} from 'react';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import type {Ranges} from './types';
import {scanForUnloadedRanges} from './utils';

// this was reworked from react-window-infinite-loader
// just want to use a hook approach that does not rely on react-window itself
// as we are using @tasnet/react-virtual going forward

export type UseInfinteLoaderParams = {
	disabled?: boolean;
	// we just use `data` to check if we need to re-check loaded data
	data: any[];
	itemCount: number;
	minimumBatchSize?: number;
	overscan: number;
	startIndex: number;
	stopIndex: number;
	threshold?: number;
	isItemLoaded?: (index: number) => boolean;
	loadMoreItems?: (
		startIndex: number,
		stopIndex: number,
	) => Promise<unknown> | void;
};

export const useInfiniteLoader = ({
	data,
	disabled,
	stopIndex,
	itemCount,
	minimumBatchSize = 10,
	startIndex,
	threshold = 15,
	isItemLoaded,
	loadMoreItems,
}: UseInfinteLoaderParams) => {
	const ensureRangeLoaded = useConstCallback(() => {
		if (disabled) return;
		const unloadedRanges = scanForUnloadedRanges({
			isItemLoaded,
			itemCount,
			minimumBatchSize,
			startIndex: Math.max(0, startIndex - threshold),
			stopIndex: Math.min(itemCount - 1, stopIndex + threshold),
		});
		loadUnloadedRanges(unloadedRanges);
	});

	const loadUnloadedRanges = (unloadedRanges: Ranges) => {
		if (!unloadedRanges.length || disabled) return;

		for (let i = 0; i < unloadedRanges.length; i += 2) {
			const startIndex = unloadedRanges[i];
			const stopIndex = unloadedRanges[i + 1];
			loadMoreItems?.(startIndex, stopIndex);
		}
	};

	useEffect(() => {
		if (disabled) return;
		ensureRangeLoaded();
	}, [
		data,
		disabled,
		itemCount,
		minimumBatchSize,
		startIndex,
		stopIndex,
		threshold,
		isItemLoaded,
		loadMoreItems,
	]);
};
